/* eslint-disable no-console */
import {CrownFilled, DownloadOutlined} from '@ant-design/icons';
import {Button, Form, Row, Col, Select, Flex, Tooltip} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {FacilityKey} from '../../../components/Forms/Constants/IP/FacilityKeyLabel';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import yearFilterArray from '../../../shared/YearFilterArray';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import {shouldBlockFreeOrReadOnly} from '../../../utils/SubscriptionHelper';
import {rowGutter} from '../../configurator/orgFacility/Constants/Common';
import {facilitySelectKey} from '../../orgManager/Utils/InfraStructureProfile/constant/facilitySelectConst';
import {sortYearOptions} from '../../orgManager/Utils/InfraStructureProfile/constant/sorYear';

import {
  FilterKey,
  FilterLabel,
  ScopeOptions,
  SubFiltersOptions
} from './common';

const FILTER_COL_SPAN = 4;
const ACTIONS_COL_SPAN = 8;

const DataLineageFilter: React.FC<{
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  setSelectedFacility: (val: string) => void;
  scope: string;
  setScope: (val: string) => void;
  setSubFilters: (val: string) => void;
  shouldDisable?: boolean;
  onDownloadXlsx: () => void;
  xlsxDownloading?: boolean;
  hasLineageData?: boolean;
  isLineageLoading?: boolean;
  onClearAll: () => void;
  hasActiveQuery?: boolean;
}> = ({
  selectedYear,
  setSelectedYear,
  setSelectedFacility,
  scope,
  setScope,
  setSubFilters,
  shouldDisable = false,
  onDownloadXlsx,
  xlsxDownloading = false,
  hasLineageData = false,
  isLineageLoading = false,
  onClearAll,
  hasActiveQuery = false
}) => {
  const [form] = useForm();
  const [searchParam] = useSearchParams();
  const facilityDetails = useCompanyStore((s) => s.facilityDetails);
  const [facilities, setFacilities] = useState<any[]>([]);

  const showNoDataTooltip =
    !isLineageLoading && !hasLineageData && Boolean(selectedYear);
  const isDownloadXlsxDisabled =
    shouldDisable ||
    xlsxDownloading ||
    !selectedYear ||
    isLineageLoading ||
    !hasLineageData;

  const handleClearAll = useCallback(() => {
    form.setFieldsValue({
      [FilterKey.facility]: undefined,
      [FilterKey.scope]: undefined,
      [FilterKey.subFilters]: undefined
    });
    onClearAll();
  }, [form, onClearAll]);

  const yearOptions = useMemo(
    () => yearFilterArray(facilityDetails, undefined, false),
    [facilityDetails]
  );

  useEffect(() => {
    if (selectedYear) {
      setFacilities(
        facilityDetails?.[selectedYear]
          ?.filter((item: any) => !item?.[FacilityKey.LEASED])
          ?.map((item: any) => {
            return {
              label: item.name,
              value: item._id
            };
          })
      );
    }
  }, [facilityDetails, selectedYear]);

  useEffect(() => {
    if (!selectedYear) {
      return;
    }
    form.setFieldsValue({
      [facilitySelectKey.year]: selectedYear
    });
  }, [form, selectedYear]);

  return (
    <Form
      form={form}
      initialValues={{
        [FilterKey.facility]: searchParam.get('facility') ?? undefined,
        [facilitySelectKey.year]: selectedYear || undefined
      }}
    >
      <Row
        gutter={rowGutter}
        style={{marginTop: '1rem', marginLeft: 'auto', marginRight: 'auto'}}
      >
        <Col className="gutter-row" span={FILTER_COL_SPAN}>
          <Form.Item
            name={facilitySelectKey.year}
            label="Year"
            labelCol={{span: 24}}
          >
            <Select
              placeholder="Year"
              options={sortYearOptions(yearOptions)}
              size="middle"
              onChange={(e) => {
                setSelectedYear(e);
                form.setFieldsValue({
                  [FilterKey.facility]: undefined,
                  [FilterKey.scope]: undefined,
                  [FilterKey.subFilters]: undefined
                });
              }}
              disabled={shouldDisable}
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" span={FILTER_COL_SPAN}>
          <Form.Item
            label={FilterLabel.facility}
            name={FilterKey.facility}
            labelCol={{span: 24}}
          >
            <Select
              placeholder={FilterLabel.facility}
              options={facilities}
              onChange={(e) => {
                setSelectedFacility(e ?? '');
              }}
              size="middle"
              allowClear
              showSearch
              filterOption={filterSelectOptions}
              disabled={shouldDisable}
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" span={FILTER_COL_SPAN}>
          <Form.Item
            label={FilterLabel.scope}
            name={FilterKey.scope}
            labelCol={{span: 24}}
          >
            <Select
              placeholder={FilterLabel.scope}
              options={ScopeOptions.map((option) => {
                return {
                  ...option,
                  label: (
                    <Flex align="center" justify="space-between">
                      <div>{option.label}</div>
                      {shouldBlockFreeOrReadOnly() && option.checkValid && (
                        <CrownFilled
                          style={{
                            color: '#FFD700',
                            paddingRight: 10,
                            paddingLeft: 10
                          }}
                          title="Upgrade Plan"
                        />
                      )}
                    </Flex>
                  ),
                  disabled: shouldBlockFreeOrReadOnly() && option.checkValid
                };
              })}
              onChange={(e) => {
                setScope(e ?? '');
                form.setFieldsValue({
                  [FilterKey.subFilters]: undefined
                });
              }}
              size="middle"
              allowClear
              disabled={shouldDisable}
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" span={FILTER_COL_SPAN}>
          <Form.Item
            label={FilterLabel.subFilters}
            name={FilterKey.subFilters}
            labelCol={{span: 24}}
          >
            <Select
              placeholder={FilterLabel.subFilters}
              options={SubFiltersOptions?.filter(
                (item: any) => item.scope === scope
              )}
              onChange={(e) => {
                setSubFilters(e ?? '');
              }}
              size="middle"
              allowClear
              showSearch
              filterOption={filterSelectOptions}
              disabled={shouldDisable}
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" span={ACTIONS_COL_SPAN}>
          <Form.Item label=" " colon={false} labelCol={{span: 24}}>
            <Flex gap={8} wrap="wrap" style={{width: '100%'}}>
              <Tooltip
                title={showNoDataTooltip ? 'No Data for this Year' : ''}
              >
                <span style={{display: 'inline-block'}}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    loading={xlsxDownloading}
                    disabled={isDownloadXlsxDisabled}
                    onClick={onDownloadXlsx}
                  >
                    Download XLSX
                  </Button>
                </span>
              </Tooltip>
              <Button
                type="default"
                onClick={handleClearAll}
                disabled={shouldDisable || !hasActiveQuery}
              >
                Clear
              </Button>
            </Flex>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default DataLineageFilter;
